import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin,
  Heart, Shield, Clock, Users
} from 'lucide-react';
import { toSafeJsonLdString } from '../utils/jsonLd';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Find Treatment', href: '/search' },
    { name: 'By State', href: '/states' },
    { name: 'Treatment Types', href: '/treatment-types' },
    { name: 'Insurance Help', href: '/insurance' },
    { name: 'Get Help Now', href: '/get-help' }
  ];

  const facilityLinks = [
    { name: 'List Your Facility', href: '/facility-signup' },
    { name: 'Facility Login', href: '/facility-login' },
    { name: 'Facility Dashboard', href: '/facility-dashboard' }
  ];

  const supportLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Sitemap', href: '/sitemap' }
  ];

  const trustIndicators = [
    { icon: Shield, text: 'Verified Centers', color: 'text-green-600' },
    { icon: Clock, text: '24/7 Support', color: 'text-blue-600' },
    { icon: Users, text: '100K+ Helped', color: 'text-purple-600' },
    { icon: Heart, text: 'Compassionate Care', color: 'text-red-600' }
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Crisis Help Banner */}
      <div className="bg-red-600 text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center gap-3 mb-2 md:mb-0">
              <Phone className="h-5 w-5" />
              <span className="font-semibold">Crisis? Get immediate help:</span>
              <a 
                href="tel:1-888-778-2494"
                className="font-bold text-yellow-300 hover:text-yellow-200 transition-colors"
              >
                1-888-778-2494
              </a>
            </div>
            <div className="text-sm text-red-100">
              Free, confidential support 24/7
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Brand & Mission */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center mb-6">
              <div className="bg-blue-600 rounded-lg p-2 mr-3">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Addiction Rehab Call</span>
            </Link>
            
            <p className="text-gray-400 mb-6 leading-relaxed">
              Connecting families with quality addiction treatment centers across the United States. 
              Free, confidential support to help you find the right path to recovery.
            </p>

            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <Phone className="h-4 w-4 mr-3 text-blue-400" />
                <span>24/7 Helpline: 1-888-778-2494</span>
              </div>
              <div className="flex items-center text-sm">
                <Mail className="h-4 w-4 mr-3 text-blue-400" />
                <span>help@addictionrehabcall.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Facilities */}
          <div>
            <h4 className="text-lg font-semibold mb-6">For Facilities</h4>
            <ul className="space-y-3">
              {facilityLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Support Links as subsection */}
            <h5 className="text-md font-medium mt-6 mb-3 text-gray-400">Support</h5>
            <ul className="space-y-2">
              {supportLinks.slice(0, 3).map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-gray-200 transition-colors text-xs"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Trust & Safety */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Trust & Safety</h4>
            
            <div className="space-y-4 mb-6">
              {trustIndicators.map((indicator, index) => (
                <div key={index} className="flex items-center">
                  <indicator.icon className={`h-4 w-4 mr-3 ${indicator.color}`} />
                  <span className="text-sm text-gray-300">{indicator.text}</span>
                </div>
              ))}
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-2">Disclaimer:</p>
              <p className="text-xs text-gray-400 leading-relaxed">
                Addiction Rehab Call is an affiliate marketing website that connects users 
                with treatment centers. We are not a treatment provider. Calls are routed to 
                our advertising partners.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="mb-2">
                © {currentYear} Addiction Rehab Call. All rights reserved. | 
                <span className="ml-1">
                  A comprehensive directory of addiction treatment centers.
                </span>
              </p>
              <div className="flex flex-wrap items-center space-x-4 text-xs">
                <Link to="/terms" className="text-gray-500 hover:text-gray-300">Terms</Link>
                <Link to="/privacy" className="text-gray-500 hover:text-gray-300">Privacy</Link>
                <Link to="/sitemap" className="text-gray-500 hover:text-gray-300">Sitemap</Link>
              </div>
            </div>
            
            <div className="flex items-center text-sm text-gray-500">
              <Heart className="h-4 w-4 mr-2 text-red-500" />
              <span>Made with care for those in recovery</span>
            </div>
          </div>
        </div>
      </div>

      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: toSafeJsonLdString({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Addiction Rehab Call",
            "url": process.env.REACT_APP_DOMAIN || window.location.origin,
            "logo": `${process.env.REACT_APP_DOMAIN || window.location.origin}/logo.png`,
            "description": "Comprehensive directory of addiction treatment centers and recovery programs.",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-888-778-2494",
              "contactType": "Customer Service",
              "availableLanguage": "English"
            }
          })
        }}
      />
    </footer>
  );
};

export default Footer;